import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconArrowRight, IconDatabase } from 'pages/import/tools/ToolsIcons';
import 'pages/import/tools/importTools.css';
import { useWorkflowCredits } from 'hooks/useWorkflowCredits';
import {
  canReadOwnershipReport,
  clock,
  noCreditsCopy,
  OwnershipInvestigationState,
  runBlockedByCredits,
  runCostLabel,
} from './entityHubSections';
import { formatVerificationDate } from './verificationReportSummary';
import BuyCreditsLink from './EntityHubBuyCreditsLink';
import EntityHubLibrary from './EntityHubLibrary';
import EntityHubOwnershipReport from './EntityHubOwnershipReport';
import EntityHubRiskResults from './EntityHubRiskResults';
import { RISK_AGENTS } from './entityRiskEvaluation';
import { EntityRiskRun } from './useEntityRiskEvaluation';
import { OwnershipInvestigation } from './useOwnershipInvestigation';
import { EntityLibrary } from './useEntityLibrary';

const TRIAL_USED_COPY =
  'Your team’s trial run of Beneficial Ownership has been used. Upgrade your plan to run it again.';

const CHECKING_COPY = {
  pill: 'Checking',
  blurb: 'Looking for an earlier run on this record',
};

const ToolActionSkeleton: React.FC = () => (
  <span className='entity-hub-tool-skeleton' aria-hidden='true' />
);

const ToolRowOpen: React.FC<{ onOpen: () => void }> = ({ onOpen }) => (
  <button
    type='button'
    className='entity-hub-tool-row-open'
    onClick={onOpen}
    tabIndex={-1}
    aria-hidden='true'
  />
);

export const ownershipRowCopy = (
  investigation: OwnershipInvestigation,
  elapsedSeconds: number,
  entityName: string,
): { pill: string; blurb: string } => {
  const startedOn = formatVerificationDate(investigation.startedAt);
  const reportOn =
    formatVerificationDate(investigation.finishedAt) || startedOn;
  const trialNote = investigation.trialUsed
    ? ' · your team’s trial run is used'
    : '';
  const state: OwnershipInvestigationState = investigation.state;
  switch (state) {
    case 'loading':
      return CHECKING_COPY;
    case 'starting':
      return { pill: 'Starting', blurb: 'Starting the investigation' };
    case 'running': {
      const since = moment(investigation.startedAt || undefined);
      return {
        pill: `Running ${clock(elapsedSeconds)}`,
        blurb: `Running in the background since ${
          since.isValid() ? since.format('h:mm A') : 'just now'
        } · we’ll email you when the report is ready`,
      };
    }
    case 'done':
      return {
        pill: 'Report ready',
        blurb: `${
          investigation.finishedHere
            ? 'Finished just now'
            : `Report from ${reportOn || 'an earlier run'}`
        } · saved to this record${
          investigation.libraryFiled ? ' and the Library' : ''
        }${trialNote}`,
      };
    case 'needs-review':
      return {
        pill: 'Needs review',
        blurb: `The last run finished with problems. Open it to see what was found.${
          investigation.trialUsed ? ` ${TRIAL_USED_COPY}` : ''
        }`,
      };
    case 'interrupted': {
      const saved = investigation.hasReport ? ' Open what it saved' : '';
      return {
        pill: 'Did not finish',
        blurb: investigation.trialUsed
          ? `The run started ${
              startedOn ? `on ${startedOn} ` : ''
            }did not finish.${saved ? `${saved}.` : ''} ${TRIAL_USED_COPY}`
          : `The run started ${
              startedOn ? `on ${startedOn} ` : ''
            }did not finish.${
              saved ? `${saved}, or run it again.` : ' Run it again.'
            }`,
      };
    }
    case 'no-access':
      return {
        pill: 'Business plan',
        blurb:
          'Part of the Business plan. Ask a team admin to upgrade to run it.',
      };
    case 'trial-used':
      return { pill: 'Trial used', blurb: TRIAL_USED_COPY };
    default:
      return {
        pill: '',
        blurb:
          investigation.startError ||
          `Traces who owns and controls ${
            entityName || 'this entity'
          } through public filings and registries, and checks the owners against sanctions lists`,
      };
  }
};

// Charged on completion; match the pricing page and the server's
// WORKFLOW_CREDIT_COSTS table.
const ENTITY_RISK_CREDIT_COST = 10;
const BENEFICIAL_OWNERSHIP_CREDIT_COST = 10;

/**
 * A run that has just settled has been charged, so the balance is re-read:
 * a team that has now run out sees it without reloading the page.
 */
const useRefreshOnSettled = (settled: boolean, refresh: () => void) => {
  useEffect(() => {
    if (settled) refresh();
  }, [settled, refresh]);
};

const RunCost: React.FC<{ cost: number }> = ({ cost }) => (
  <span className='entity-hub-tool-cost'>{runCostLabel(cost)}</span>
);

const UpgradeLink: React.FC<{ primary: boolean }> = ({ primary }) => (
  <Link
    className={`entity-hub-btn${primary ? ' primary' : ''}`}
    to={{ pathname: '/profile', state: { linkTab: 'Subscriptions' } }}
  >
    Upgrade plan
    <ArrowForwardIcon />
  </Link>
);

interface OwnershipRunAgainProps {
  investigation: OwnershipInvestigation;
  primary: boolean;
  noCredits: boolean;
  onRun: () => void;
}

/**
 * Run again, the upgrade once the team's trial run is used, or the purchase
 * link when the balance will not cover another run.
 */
const OwnershipRunAgain: React.FC<OwnershipRunAgainProps> = ({
  investigation,
  primary,
  noCredits,
  onRun,
}) =>
  investigation.trialUsed ? (
    <UpgradeLink primary={primary} />
  ) : noCredits ? (
    <BuyCreditsLink primary={primary} />
  ) : (
    <button
      type='button'
      className={`entity-hub-btn${primary ? ' primary' : ''}`}
      onClick={onRun}
    >
      <RefreshIcon />
      Run again
      <RunCost cost={BENEFICIAL_OWNERSHIP_CREDIT_COST} />
    </button>
  );

interface OwnershipRowProps {
  entityName: string;
  investigation: OwnershipInvestigation;
  elapsedSeconds: number;
  onOpenReport: () => void;
  cost: number;
  noCredits: boolean;
}

const OwnershipRow: React.FC<OwnershipRowProps> = ({
  entityName,
  investigation,
  elapsedSeconds,
  onOpenReport,
  cost,
  noCredits,
}) => {
  const { state, startError } = investigation;
  const checking = state === 'loading';
  const running = state === 'running' || state === 'starting';
  const review = state === 'needs-review' || state === 'interrupted';
  const finished = state === 'done' || review;
  const blocked =
    !checking &&
    runBlockedByCredits(noCredits, {
      running: running,
      finished: finished,
    });
  const outOfCredits = noCreditsCopy(cost);
  const { pill, blurb } = blocked
    ? outOfCredits
    : ownershipRowCopy(investigation, elapsedSeconds, entityName);
  const openable = canReadOwnershipReport(investigation);

  const actions = checking ? (
    <ToolActionSkeleton />
  ) : state === 'no-access' || running ? null : blocked ? (
    <BuyCreditsLink primary />
  ) : state === 'trial-used' ? (
    <UpgradeLink primary />
  ) : finished ? (
    <>
      <OwnershipRunAgain
        investigation={investigation}
        primary={state === 'interrupted' && !investigation.hasReport}
        noCredits={noCredits}
        onRun={investigation.start}
      />
      {state === 'interrupted' && !investigation.hasReport ? null : (
        <button
          type='button'
          className='entity-hub-btn primary'
          onClick={onOpenReport}
        >
          {review ? 'Review report' : 'View report'}
          <ArrowForwardIcon />
        </button>
      )}
    </>
  ) : (
    <button
      type='button'
      className='entity-hub-btn primary'
      onClick={investigation.start}
      disabled={!entityName}
    >
      <PlayArrowRoundedIcon />
      {startError ? 'Try again' : 'Run in background'}
      <RunCost cost={cost} />
    </button>
  );

  return (
    <div
      className={`iml-row is-ownership${running ? ' is-active is-live' : ''}${
        openable ? ' is-openable' : ''
      }${
        state === 'no-access' || state === 'trial-used' || blocked
          ? ' is-disabled'
          : ''
      }`}
    >
      {openable ? <ToolRowOpen onOpen={onOpenReport} /> : null}
      <div className='iml-row-head entity-hub-tool-head'>
        <div className='iml-row-icon' aria-hidden='true'>
          <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
          {running ? (
            <span className='entity-hub-tool-ping' aria-hidden='true' />
          ) : null}
          {state === 'done' ? (
            <span className='entity-hub-tool-badge'>
              <CheckIcon />
            </span>
          ) : review ? (
            <span className='entity-hub-tool-badge is-review'>
              <PriorityHighIcon />
            </span>
          ) : null}
        </div>
        <div className='iml-row-text'>
          <div className='iml-row-title'>
            <h3 className='iml-row-name'>Beneficial Ownership investigation</h3>
            {pill ? <span className='entity-hub-tool-pill'>{pill}</span> : null}
          </div>
          <p
            aria-live='polite'
            className={`iml-row-blurb${
              startError && state === 'idle' ? ' is-error' : ''
            }`}
          >
            {blurb}
          </p>
        </div>
        <div className='entity-hub-tool-actions'>{actions}</div>
      </div>
    </div>
  );
};

type RiskPhase = 'checking' | 'blocked' | 'running' | 'done' | 'idle';

interface EntityHubDeepDiveProps {
  entityName: string;
  entityCountry?: string | null;
  investigation: OwnershipInvestigation;
  investigationElapsedSeconds: number;
  library: EntityLibrary;
  currentUserId?: string;
  canManageDocuments?: boolean;
  risk: EntityRiskRun;
  riskElapsedSeconds: number;
  entityLoading?: boolean;
}

/**
 * The record's Deep Dive: the Beneficial Ownership investigation, Export and
 * Transshipment Risk, and the Library of documents filed against the record.
 */
const EntityHubDeepDive: React.FC<EntityHubDeepDiveProps> = ({
  entityName,
  entityCountry,
  investigation,
  investigationElapsedSeconds,
  library,
  currentUserId,
  canManageDocuments,
  risk,
  riskElapsedSeconds,
  entityLoading,
}) => {
  // The Deep Dive's own page for a tool's results, opened from its row.
  const [view, setView] = useState<'shelf' | 'risk' | 'ownership' | 'library'>(
    'shelf',
  );
  const riskCredits = useWorkflowCredits(ENTITY_RISK_CREDIT_COST);
  // The bucket lets a trialing team's unused free run through the gate, as on
  // the standalone Beneficial Ownership workflow.
  const ownershipCredits = useWorkflowCredits(
    BENEFICIAL_OWNERSHIP_CREDIT_COST,
    'beneficial_ownership',
  );

  const documentCount = library.documents.length;
  const reportReadable = canReadOwnershipReport(investigation);
  // Once the report can no longer be read (another record, a new run), the
  // page goes back to the shelf rather than reopening by itself later.
  useEffect(() => {
    if (view === 'ownership' && !reportReadable) setView('shelf');
  }, [reportReadable, view]);

  useRefreshOnSettled(risk.state === 'done', riskCredits.refresh);
  useRefreshOnSettled(investigation.state === 'done', ownershipCredits.refresh);

  const riskRunning = risk.state === 'running';
  const riskDone = risk.state === 'done';
  const riskPhase: RiskPhase =
    Boolean(entityLoading) && !riskRunning && !riskDone
      ? 'checking'
      : riskRunning
      ? 'running'
      : riskDone
      ? 'done'
      : runBlockedByCredits(riskCredits.insufficient, {
          running: false,
          finished: false,
        })
      ? 'blocked'
      : 'idle';
  const riskOpenable =
    riskPhase === 'running' ||
    (riskPhase === 'done' && Boolean(risk.evaluation));
  const runRisk = () => {
    risk.run();
    riskCredits.refresh();
  };
  const riskOverall = risk.evaluation?.overallRisk || null;
  const riskNoCreditsCopy = noCreditsCopy(ENTITY_RISK_CREDIT_COST);
  const riskSub =
    riskPhase === 'checking'
      ? CHECKING_COPY.blurb
      : riskPhase === 'blocked'
      ? riskNoCreditsCopy.blurb
      : riskPhase === 'running'
      ? `Running ${RISK_AGENTS.length} agents in parallel · the results save to this record`
      : riskPhase === 'done' && risk.evaluation
      ? [
          riskOverall ? `${riskOverall} risk` : 'No combined verdict',
          `${risk.evaluation.answered} of ${RISK_AGENTS.length} agents answered`,
          risk.finishedHere
            ? 'finished just now'
            : risk.evaluation.checkedAt
            ? `checked ${formatVerificationDate(risk.evaluation.checkedAt)}`
            : '',
          risk.libraryFiled
            ? 'saved to this record and the Library'
            : 'saved to this record',
        ]
          .filter(Boolean)
          .join(' · ')
      : risk.error ||
        'Export control and diversion, adverse media, financial standing and personnel, searched in parallel and combined into one verdict';

  if (view === 'library' && library.available) {
    return (
      <EntityHubLibrary
        entityName={entityName}
        library={library}
        currentUserId={currentUserId}
        canManageDocuments={canManageDocuments}
        onBack={() => setView('shelf')}
      />
    );
  }

  if (view === 'risk') {
    return (
      <EntityHubRiskResults
        entityName={entityName}
        entityCountry={entityCountry}
        evaluation={risk.evaluation}
        running={riskRunning}
        elapsedSeconds={riskElapsedSeconds}
        libraryFiled={risk.libraryFiled}
        onBack={() => setView('shelf')}
        onRunAgain={runRisk}
        noCredits={riskCredits.insufficient}
      />
    );
  }

  // A new run replaces the report this page reads, so it hands back to the
  // shelf, where the row shows the run until its report is ready.
  if (view === 'ownership' && reportReadable) {
    return (
      <EntityHubOwnershipReport
        key={investigation.ticketId || ''}
        investigation={investigation}
        onBack={() => setView('shelf')}
        runAgain={
          <OwnershipRunAgain
            investigation={investigation}
            primary={false}
            noCredits={ownershipCredits.insufficient}
            onRun={() => {
              setView('shelf');
              investigation.start();
            }}
          />
        }
      />
    );
  }

  const riskStatus =
    riskPhase === 'checking'
      ? CHECKING_COPY.pill
      : riskPhase === 'blocked'
      ? riskNoCreditsCopy.pill
      : riskPhase === 'running'
      ? `Running ${clock(riskElapsedSeconds)}`
      : riskPhase === 'done'
      ? risk.evaluation?.failed
        ? 'Partial'
        : 'Results ready'
      : '';

  // The Import Hub tool shelf (pages/import/tools), on this record: the same
  // rows, accents and hover, with each check's state and actions on its row.
  return (
    <div className='iml-root iml-style-island entity-hub-deep-dive'>
      <div className='iml-shelf'>
        <OwnershipRow
          entityName={entityName}
          investigation={investigation}
          elapsedSeconds={investigationElapsedSeconds}
          onOpenReport={() => setView('ownership')}
          cost={BENEFICIAL_OWNERSHIP_CREDIT_COST}
          noCredits={ownershipCredits.insufficient}
        />

        <div
          className={`iml-row is-risk${
            riskPhase === 'running' ? ' is-active is-live' : ''
          }${riskOpenable ? ' is-openable' : ''}${
            riskPhase === 'blocked' ? ' is-disabled' : ''
          }`}
        >
          {riskOpenable ? <ToolRowOpen onOpen={() => setView('risk')} /> : null}
          <div className='iml-row-head entity-hub-tool-head'>
            <div className='iml-row-icon' aria-hidden='true'>
              <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
              {riskPhase === 'running' ? (
                <span className='entity-hub-tool-ping' aria-hidden='true' />
              ) : null}
              {riskPhase === 'done' ? (
                <span
                  className={`entity-hub-tool-badge${
                    risk.evaluation?.failed ? ' is-review' : ''
                  }`}
                >
                  {risk.evaluation?.failed ? (
                    <PriorityHighIcon />
                  ) : (
                    <CheckIcon />
                  )}
                </span>
              ) : null}
            </div>
            <div className='iml-row-text'>
              <div className='iml-row-title'>
                <h3 className='iml-row-name'>Export and Transshipment Risk</h3>
                {riskStatus ? (
                  <span className='entity-hub-tool-pill'>{riskStatus}</span>
                ) : null}
              </div>
              <p
                aria-live='polite'
                className={`iml-row-blurb${
                  risk.error && riskPhase === 'idle' ? ' is-error' : ''
                }`}
              >
                {riskSub}
              </p>
            </div>
            <div className='entity-hub-tool-actions'>
              {riskPhase === 'checking' ? (
                <ToolActionSkeleton />
              ) : riskPhase === 'blocked' ? (
                <BuyCreditsLink primary />
              ) : riskPhase === 'running' ? (
                <button
                  type='button'
                  className='entity-hub-btn'
                  onClick={() => setView('risk')}
                >
                  Watch
                  <ArrowForwardIcon />
                </button>
              ) : riskPhase === 'done' ? (
                <>
                  {riskCredits.insufficient ? (
                    <BuyCreditsLink />
                  ) : (
                    <button
                      type='button'
                      className='entity-hub-btn'
                      onClick={runRisk}
                    >
                      <RefreshIcon />
                      Run again
                      <RunCost cost={ENTITY_RISK_CREDIT_COST} />
                    </button>
                  )}
                  <button
                    type='button'
                    className='entity-hub-btn primary'
                    onClick={() => setView('risk')}
                  >
                    View report
                    <ArrowForwardIcon />
                  </button>
                </>
              ) : (
                <button
                  type='button'
                  className='entity-hub-btn primary'
                  onClick={runRisk}
                  disabled={!entityName}
                >
                  <PlayArrowRoundedIcon />
                  {risk.error ? 'Try again' : 'Run check'}
                  <RunCost cost={ENTITY_RISK_CREDIT_COST} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className={`iml-row is-library${
            library.available ? ' is-openable' : ''
          }`}
        >
          <button
            type='button'
            className='iml-row-head'
            onClick={() => setView('library')}
            disabled={!library.available}
          >
            <div className='iml-row-icon' aria-hidden='true'>
              <IconDatabase className='iml-icon' />
            </div>
            <div className='iml-row-text'>
              <div className='iml-row-title'>
                <div className='iml-row-name'>Library</div>
                {documentCount > 0 ? (
                  <span className='entity-hub-tool-pill'>
                    {documentCount.toLocaleString()}{' '}
                    {documentCount === 1 ? 'file' : 'files'}
                  </span>
                ) : null}
              </div>
              <div className='iml-row-blurb'>
                {library.available
                  ? 'Upload and browse the files kept against this record, with each Beneficial Ownership and Export and Transshipment Risk report once it finishes'
                  : 'Unavailable: this record is not linked to a database entity'}
              </div>
            </div>
            <IconArrowRight className='iml-row-arrow' />
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntityHubDeepDive;
